import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Avatar } from '@/components/ui/Avatar'
import { Mail, Phone, MapPin, Building2, Hash } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import type { User, Project, Facture, Devis } from '@/types/database-real'
import type { ClientMetrics } from '@/hooks/useClientDetail'

interface ClientOverviewTabProps {
  client: User
  projects: Project[]
  factures: Facture[]
  devis: Devis[]
  assignedAdmins: User[]
  metrics: ClientMetrics
}

interface StatProps {
  label: string
  value: string | number
  variant?: 'default' | 'warning'
}

function Stat({ label, value, variant = 'default' }: StatProps) {
  return (
    <div className="text-center p-3 bg-beige-50 rounded-lg">
      <p className="text-sm text-brown-500">{label}</p>
      <p
        className={`text-xl font-semibold ${
          variant === 'warning' ? 'text-red-600' : 'text-brown-900'
        }`}
      >
        {value}
      </p>
    </div>
  )
}

interface InfoRowProps {
  icon: React.ReactNode
  label: string
  value: string
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-beige-100 last:border-0">
      <div className="flex items-center gap-2 text-brown-500">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <span className="text-brown-900">{value}</span>
    </div>
  )
}

export function ClientOverviewTab({
  client,
  assignedAdmins,
  metrics,
}: ClientOverviewTabProps) {
  const projectsCount = metrics.totalQuotes
  const activeProjectsCount = metrics.activeProjects
  const totalCA = metrics.totalRevenue
  const unpaidInvoices = metrics.unpaidInvoices
  // Pour l'instant, on prend le premier admin assigné comme owner
  const ownerAdmin = assignedAdmins[0]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Statistics */}
      <Card padding="md">
        <CardHeader>
          <CardTitle>Statistiques</CardTitle>
        </CardHeader>
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Projets" value={projectsCount} />
          <Stat label="Projets actifs" value={activeProjectsCount} />
          <Stat label="CA Total" value={formatCurrency(totalCA)} />
          <Stat
            label="Factures impayées"
            value={unpaidInvoices}
            variant={unpaidInvoices > 0 ? 'warning' : 'default'}
          />
        </div>
      </Card>

      {/* Contact Information */}
      <Card padding="md">
        <CardHeader>
          <CardTitle>Contact</CardTitle>
        </CardHeader>
        <div className="space-y-1">
          <InfoRow
            icon={<Mail className="w-4 h-4" />}
            label="Email"
            value={client.email || '-'}
          />
          <InfoRow
            icon={<Phone className="w-4 h-4" />}
            label="Téléphone"
            value={client.phone || '-'}
          />
          <InfoRow
            icon={<MapPin className="w-4 h-4" />}
            label="Adresse"
            value={client.address || client.adresse_facturation || '-'}
          />
        </div>
      </Card>

      {/* Company Information (if professional) */}
      {client.type_client === 'professionnel' && (
        <Card padding="md">
          <CardHeader>
            <CardTitle>Entreprise</CardTitle>
          </CardHeader>
          <div className="space-y-1">
            <InfoRow
              icon={<Building2 className="w-4 h-4" />}
              label="Société"
              value={client.company || '-'}
            />
            <InfoRow
              icon={<Hash className="w-4 h-4" />}
              label="SIRET"
              value={client.siret || '-'}
            />
            <InfoRow
              icon={<Hash className="w-4 h-4" />}
              label="TVA Intra."
              value={client.tva_intracommunautaire || '-'}
            />
            {client.adresse_facturation && (
              <InfoRow
                icon={<MapPin className="w-4 h-4" />}
                label="Adresse de facturation"
                value={client.adresse_facturation}
              />
            )}
          </div>
        </Card>
      )}

      {/* Management */}
      <Card padding="md">
        <CardHeader>
          <CardTitle>Gestion</CardTitle>
        </CardHeader>
        <div className="flex items-center justify-between py-2">
          <span className="text-brown-600">Admin principal</span>
          {ownerAdmin ? (
            <div className="flex items-center gap-2">
              <Avatar
                src={ownerAdmin.avatar_url}
                name={ownerAdmin.full_name}
                size="sm"
              />
              <span className="text-brown-900">{ownerAdmin.full_name || ownerAdmin.email}</span>
            </div>
          ) : (
            <span className="text-brown-400">Non assigné</span>
          )}
        </div>
      </Card>
    </div>
  )
}
